import React, { useMemo, useState } from 'react';
import { Button, Empty, Space, Switch, Tag, Timeline, Typography } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import dayjs from 'dayjs';
import DocumentList from '@/components/documents/DocumentList';
import { formatDate } from '@/utils/format/date';
import type { CrmActivity } from '@/services/crmActivity.service';

const { Text, Paragraph } = Typography;

interface Props {
  activities: CrmActivity[];
  /** Show which lead / opportunity / customer each activity belongs to (Customer 360, registers). */
  showRecordLinks?: boolean;
  /** Stage changes and automation entries; hidden by default behind a toggle. */
  hideSystemByDefault?: boolean;
  emptyText?: string;
}

const SENTIMENT_COLOR: Record<string, string> = { Positive: 'green', Neutral: 'default', Negative: 'red' };

export function crmPersonName(p?: { first_name?: string; last_name?: string; email?: string } | null): string {
  if (!p) return '';
  return [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || '';
}

export function CrmFollowUpTag({ a }: { a: CrmActivity }) {
  if (!a.follow_up_date) return null;
  const label = a.is_commitment ? 'Commitment' : 'Follow-up';
  if (a.follow_up_status === 'Done') return <Tag color="green">{label} done</Tag>;
  if (a.follow_up_status === 'Cancelled') return <Tag>{label} cancelled</Tag>;
  if (a.follow_up_status === 'Rescheduled') return <Tag color="default">{label} rescheduled</Tag>;
  return (
    <Tag color={a.is_overdue ? 'red' : a.is_commitment ? 'purple' : 'blue'}>
      {label} {a.is_overdue ? 'overdue' : 'due'} {formatDate(a.follow_up_date)}
    </Tag>
  );
}

export function CrmRecordLinks({ a }: { a: CrmActivity }) {
  return (
    <Space size={4} wrap>
      {a.opportunity && (
        <Link to={`/growth/opportunities/${a.opportunity.id}`}>{a.opportunity.opportunity_number}</Link>
      )}
      {a.lead && !a.opportunity && <Link to={`/growth/leads/${a.lead.id}`}>{a.lead.lead_number}</Link>}
      {a.customer && <Link to={`/growth/customers/${a.customer.id}/360`}>{a.customer.customer_name}</Link>}
      {!a.customer && (a.opportunity?.customer_name || a.lead?.customer_name) && (
        <Text type="secondary">{a.opportunity?.customer_name || a.lead?.customer_name}</Text>
      )}
    </Space>
  );
}

const CrmActivityTimeline: React.FC<Props> = ({
  activities,
  showRecordLinks = false,
  hideSystemByDefault = true,
  emptyText = 'No activities logged yet',
}) => {
  const [showSystem, setShowSystem] = useState(!hideSystemByDefault);
  const user = useAuthStore((s) => s.user);
  const canEdit = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.edit'));
  const [openAttachments, setOpenAttachments] = useState<Record<string, boolean>>({});
  const systemCount = useMemo(
    () => activities.filter((a) => a.activity_type_code === 'SYSTEM').length,
    [activities]
  );
  const visible = useMemo(
    () => (showSystem ? activities : activities.filter((a) => a.activity_type_code !== 'SYSTEM')),
    [activities, showSystem]
  );

  return (
    <>
      {systemCount > 0 && (
        <Space style={{ marginBottom: 12 }}>
          <Switch size="small" checked={showSystem} onChange={setShowSystem} />
          <Text type="secondary">Show stage changes and automatic entries ({systemCount})</Text>
        </Space>
      )}
      {!visible.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
      ) : (
        <Timeline
          items={visible.map((a) => {
            const isSystem = a.activity_type_code === 'SYSTEM';
            return {
              key: a.id,
              color: isSystem ? 'gray' : a.is_overdue ? 'red' : a.counts_as_customer_contact ? 'blue' : 'green',
              children: (
                <div>
                  <Space size={6} wrap>
                    <Tag color={isSystem ? 'default' : 'geekblue'}>{a.activity_type_name}</Tag>
                    <Text type="secondary">
                      {formatDate(a.activity_date)} {dayjs(a.activity_date).format('HH:mm')}
                    </Text>
                    {a.duration_minutes ? <Text type="secondary">· {a.duration_minutes} min</Text> : null}
                    {a.sentiment && <Tag color={SENTIMENT_COLOR[a.sentiment]}>{a.sentiment}</Tag>}
                    <CrmFollowUpTag a={a} />
                  </Space>
                  {showRecordLinks && (
                    <div>
                      <CrmRecordLinks a={a} />
                    </div>
                  )}
                  {a.subject ? <div><Text strong>{a.subject}</Text></div> : null}
                  {a.contact_name ? <Text type="secondary">With {a.contact_name}</Text> : null}
                  {a.description ? (
                    <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 4 }} ellipsis={{ rows: 4, expandable: true }}>
                      {a.description}
                    </Paragraph>
                  ) : null}
                  {a.customer_requirements ? (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      <Text type="secondary">Requirements: </Text>
                      {a.customer_requirements}
                    </div>
                  ) : null}
                  {a.outcome ? (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      <Text type="secondary">Outcome: </Text>
                      {a.outcome}
                    </div>
                  ) : null}
                  {a.commitment_text ? (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      <Text type="secondary">{a.is_commitment ? 'Committed: ' : 'Next action: '}</Text>
                      {a.commitment_text}
                      {a.follow_up_owner_user && (
                        <Text type="secondary"> · owner {crmPersonName(a.follow_up_owner_user)}</Text>
                      )}
                    </div>
                  ) : null}
                  {a.completion_note ? (
                    <div>
                      <Text type="secondary">Closed note: </Text>
                      {a.completion_note}
                    </div>
                  ) : null}
                  <Space size={8}>
                    {a.performer && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {crmPersonName(a.performer)}
                      </Text>
                    )}
                    {!isSystem && canEdit && (
                      <Link to={`/growth/activities/${a.id}/edit`}>Edit</Link>
                    )}
                    {!isSystem && (
                      <Button
                        type="link"
                        size="small"
                        icon={<PaperClipOutlined />}
                        onClick={() => setOpenAttachments((p) => ({ ...p, [a.id]: !p[a.id] }))}
                      >
                        Attachments
                      </Button>
                    )}
                  </Space>
                  {openAttachments[a.id] && <DocumentList entityType="CrmActivity" entityId={a.id} />}
                </div>
              ),
            };
          })}
        />
      )}
    </>
  );
};

export default CrmActivityTimeline;
